import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { fetchClientsByCpf, fetchClientsByName, fetchWorkspacesWithAdverseParties } from '../services/ConflictRepository';

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const cleanDigits = (value) => String(value || '').replace(/\D/g, '');

const pickStrongestToken = (value) => {
  const parts = normalizeText(value).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts.sort((a, b) => b.length - a.length)[0] || '';
};

const buildClientLabel = (data) => {
  const physicalName = String(data?.qualification?.physical?.name || '').trim();
  const companyName = String(data?.qualification?.legal?.companyName || '').trim();
  return physicalName || companyName || String(data?.anchor?.name || data?.name || 'Cliente').trim();
};

const buildClientDocument = (data) => {
  return String(data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '').trim();
};

const buildConflictSummary = (matches) => {
  // BELT-AND-SUSPENDERS: garante que nenhum fantasma (isGhostRecord:true) gere conflito
  const liveMatches = Array.isArray(matches) ? matches.filter(m => m.isGhostRecord !== true) : [];
  if (!Array.isArray(liveMatches) || liveMatches.length === 0) {
    return {
      hasConflict: false,
      status: 'clear',
      type: null,
      title: '',
      message: '',
      matches: []
    };
  }

  const absoluta = liveMatches.find((item) => item.type === 'DUPLICIDADE_ABSOLUTA');
  if (absoluta) {
    return {
      hasConflict: true,
      status: 'danger',
      type: 'DUPLICIDADE_ABSOLUTA',
      title: 'H3 — Duplicidade Absoluta',
      message: absoluta.message,
      culpritId: absoluta.entityId,
      matches: liveMatches
    };
  }

  const adverse = liveMatches.find((item) => item.type === 'PARTE_ADVERSA');
  if (adverse) {
    return {
      hasConflict: true,
      status: 'danger',
      type: 'PARTE_ADVERSA',
      title: 'Alerta de Conflito Ético',
      message: adverse.message,
      matches: liveMatches
    };
  }

  const existing = liveMatches.find((item) => item.type === 'EX_CLIENTE');
  if (existing) {
    return {
      hasConflict: true,
      status: 'warning',
      type: 'EX_CLIENTE',
      title: 'H1 — Colisão de Documento (CPF/CNPJ)',
      message: existing.message,
      culpritId: existing.entityId,
      matches: liveMatches
    };
  }

  // HOMONIMO é apenas informativo — NÃO deve acionar IMPEDIMENTO ÉTICO
  const homonimo = liveMatches.find((item) => item.type === 'HOMONIMO');
  return {
    hasConflict: false,
    status: 'info',
    type: 'HOMONIMO',
    title: 'H2 — Homônimo Detectado',
    message: homonimo?.message || 'Nome semelhante encontrado na base. Verifique se é a mesma pessoa.',
    culpritId: homonimo?.entityId,
    matches: liveMatches
  };
};

export async function scanForConflicts(searchName, searchCpf, options = {}) {
  const safeAppId = String(options?.appId || FIREBASE_CONFIG?.projectId || '').trim();
  const excludeClientId = String(options?.excludeClientId || '').trim();
  const safeName = String(searchName || '').trim();
  const safeDocDigits = cleanDigits(searchCpf);
  const strongestToken = pickStrongestToken(safeName);
  const normalizedName = normalizeText(safeName);
  const matches = [];

  if (!db || !safeAppId) {
    return buildConflictSummary([]);
  }

  try {
    if (safeDocDigits.length === 11 || safeDocDigits.length === 14) {
      const cpfRows = await fetchClientsByCpf(searchCpf, safeDocDigits, db, safeAppId);
      const seenCpfIds = new Set();
      cpfRows.forEach((row) => {
        const rowId = String(row.id || '').trim();
        if (seenCpfIds.has(rowId)) return;
        seenCpfIds.add(rowId);
        if (excludeClientId && rowId === excludeClientId) return;
        if (row.isDeleted === true) {
          console.log(`[RDA SCANNER] Conflito ignorado (Registro Deletado): CPF=${buildClientDocument(row)} ID=${rowId}`);
          return;
        }
        matches.push({
          status: 'warning',
          type: 'EX_CLIENTE',
          source: 'clients',
          entityId: rowId,
          entityName: buildClientLabel(row),
          entityDocument: buildClientDocument(row),
          isGhostRecord: false,
          message: `Documento já cadastrado na base de clientes: ${buildClientLabel(row)}.`
        });
      });
    }

    if (strongestToken && safeName.length >= 4) {
      const nameRows = await fetchClientsByName(strongestToken, db, safeAppId);
      nameRows.forEach((row) => {
        const rowId = String(row.id || '').trim();
        if (excludeClientId && rowId === excludeClientId) return;
        if (row.isDeleted === true) {
          console.log(`[RDA SCANNER] Conflito ignorado (Registro Deletado por nome): ID=${rowId}`);
          return;
        }
        const rowCpfDigits = cleanDigits(row?.qualification?.physical?.cpf || '');
        const rowName = normalizeText(buildClientLabel(row));
        const normalizedInput = normalizeText(safeName);
        if (!rowName || !normalizedInput) return;
        const nameMatch = rowName.includes(normalizedInput) || normalizedInput.includes(rowName);
        if (!nameMatch) return;
        const cpfMatch = safeDocDigits.length === 11 && rowCpfDigits.length === 11 && safeDocDigits === rowCpfDigits;
        if (cpfMatch) {
          matches.push({
            status: 'danger',
            type: 'DUPLICIDADE_ABSOLUTA',
            source: 'clients',
            entityId: rowId,
            entityName: buildClientLabel(row),
            entityDocument: buildClientDocument(row),
            isGhostRecord: false,
            message: `H3 — Duplicidade Absoluta: mesmo nome e CPF já cadastrado para "${buildClientLabel(row)}" (ID: ${rowId}).`
          });
        } else {
          const alreadyCpfConflict = matches.some((m) => m.type === 'EX_CLIENTE' && m.entityId === rowId);
          if (!alreadyCpfConflict) {
            matches.push({
              status: 'info',
              type: 'HOMONIMO',
              source: 'clients',
              entityId: rowId,
              entityName: buildClientLabel(row),
              entityDocument: buildClientDocument(row),
              isGhostRecord: false,
              message: `H2 — Homônimo: "${buildClientLabel(row)}" (ID: ${rowId}) tem nome semelhante mas CPF diferente. Verifique se são pessoas distintas.`
            });
          }
        }
      });
    }

    if (strongestToken || safeDocDigits) {
      // Fusão Processos/Caderno (MC25b): fonte real é autos_workspaces
      // (processes está vazia). partes.adversas é array de {nome,tipo,
      // documento}, não mais um campo parteContraria string único.
      const workspaceRows = await fetchWorkspacesWithAdverseParties(db, safeAppId);
      workspaceRows.forEach((row) => {
        const processTitle = String(row?.numeroCNJ || row?.alias || 'Caderno de Autos').trim();
        const adversas = Array.isArray(row?.partes?.adversas) ? row.partes.adversas : [];
        adversas.forEach((adversa) => {
          const adverseName = String(adversa?.nome || '').trim();
          const adverseDoc = String(adversa?.documento || '').trim();
          const normalizedAdverse = normalizeText(adverseName);

          const nameMatched = normalizedName && normalizedAdverse.includes(normalizedName);
          const docMatched = safeDocDigits && cleanDigits(adverseDoc || adverseName).includes(safeDocDigits);

          if (!nameMatched && !docMatched) return;

          matches.push({
            status: 'danger',
            type: 'PARTE_ADVERSA',
            source: 'autos_workspaces',
            entityId: String(row.id || '').trim(),
            entityName: adverseName || 'Parte contrária',
            processLabel: processTitle,
            message: `Atenção: ${adverseName || 'esta pessoa'} já figura como parte contrária no processo/caderno ${processTitle}.`
          });
        });
      });
    }

    const deduped = [];
    const seen = new Set();
    matches.forEach((item) => {
      const key = `${item.type}::${item.source}::${item.entityId}::${item.entityName}`;
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });

    return buildConflictSummary(deduped);
  } catch (error) {
    return {
      hasConflict: false,
      status: 'clear',
      type: null,
      title: '',
      message: '',
      matches: [],
      error: String(error?.message || error || 'Falha ao analisar conflito de interesses.')
    };
  }
}

export default scanForConflicts;
